import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { CheckCircle2, XCircle, Loader2, X } from "lucide-react";

// Toast de succès impératif (même esprit que le helper `dialog`) : monté
// directement sur document.body, il survit à la fermeture des overlays qui
// l'ont déclenché. Usage : showSuccessToast("Titre", "Message").
const AUTO_DISMISS_MS = 6000;

function ToastCard({ titre, message, onDone }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    const hide = setTimeout(() => setVisible(false), AUTO_DISMISS_MS);
    const done = setTimeout(onDone, AUTO_DISMISS_MS + 400);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(hide);
      clearTimeout(done);
    };
  }, [onDone]);

  const close = () => {
    setVisible(false);
    setTimeout(onDone, 400);
  };

  return (
    <div
      role="status"
      style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        zIndex: 100000,
        transition: "transform .35s ease, opacity .35s ease",
        transform: visible ? "translateY(0)" : "translateY(16px)",
        opacity: visible ? 1 : 0,
      }}
      className="flex items-start gap-3 bg-white border border-slate-200 shadow-2xl rounded-xl pl-4 pr-3 py-3 max-w-sm"
    >
      <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
        <CheckCircle2 className="w-4.5 h-4.5 text-emerald-600" size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 m-0">{titre}</p>
        <p className="text-xs text-slate-500 m-0 mt-0.5 leading-relaxed">
          {message}
        </p>
      </div>
      <button
        onClick={close}
        aria-label="Fermer"
        className="text-slate-300 hover:text-slate-500 transition-colors flex-shrink-0 mt-0.5"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export function showSuccessToast(titre, message) {
  const node = document.createElement("div");
  document.body.appendChild(node);
  const root = createRoot(node);
  const cleanup = () => {
    root.unmount();
    node.remove();
  };
  root.render(<ToastCard titre={titre} message={message} onDone={cleanup} />);
}

const STATUS_STYLES = {
  loading: { icon: Loader2, iconClass: "text-slate-500 animate-spin", bg: "bg-slate-100" },
  success: { icon: CheckCircle2, iconClass: "text-emerald-600", bg: "bg-emerald-100" },
  error: { icon: XCircle, iconClass: "text-red-600", bg: "bg-red-100" },
};

function StatusToastCard({ initial, onDone, controlsRef }) {
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState(initial);
  const dismissTimers = useRef([]);

  const clearDismissTimers = () => {
    dismissTimers.current.forEach(clearTimeout);
    dismissTimers.current = [];
  };

  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    controlsRef.current = {
      update: (next) => {
        setState(next);
        clearDismissTimers();
        // "loading" reste affiché tant qu'on n'a pas de résultat définitif ;
        // succès/erreur se referment tout seuls après un délai, comme le
        // toast de succès existant.
        if (next.status !== "loading") {
          const hide = setTimeout(() => setVisible(false), AUTO_DISMISS_MS);
          const done = setTimeout(onDone, AUTO_DISMISS_MS + 400);
          dismissTimers.current = [hide, done];
        }
      },
    };
    return clearDismissTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = () => {
    clearDismissTimers();
    setVisible(false);
    setTimeout(onDone, 400);
  };

  const { icon: Icon, iconClass, bg } = STATUS_STYLES[state.status] || STATUS_STYLES.loading;

  return (
    <div
      role="status"
      style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        zIndex: 100000,
        transition: "transform .35s ease, opacity .35s ease",
        transform: visible ? "translateY(0)" : "translateY(16px)",
        opacity: visible ? 1 : 0,
      }}
      className="flex items-start gap-3 bg-white border border-slate-200 shadow-2xl rounded-xl pl-4 pr-3 py-3 max-w-sm"
    >
      <div className={`w-9 h-9 rounded-full ${bg} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-4.5 h-4.5 ${iconClass}`} size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 m-0">{state.titre}</p>
        {state.message && (
          <p className="text-xs text-slate-500 m-0 mt-0.5 leading-relaxed">
            {state.message}
          </p>
        )}
      </div>
      {state.status !== "loading" && (
        <button
          onClick={close}
          aria-label="Fermer"
          className="text-slate-300 hover:text-slate-500 transition-colors flex-shrink-0 mt-0.5"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

// Toast à état évolutif : affiche d'abord un spinner ("loading"), puis se met
// à jour en place vers "success" ou "error" via l'objet retourné — utile pour
// suivre une opération asynchrone (ex. sauvegarde) du clic jusqu'au résultat.
// Usage :
//   const toast = showStatusToast({ status: "loading", titre: "...", message: "..." });
//   toast.update({ status: "success", titre: "...", message: "..." });
export function showStatusToast(initial) {
  const node = document.createElement("div");
  document.body.appendChild(node);
  const root = createRoot(node);
  const controlsRef = { current: { update: () => {} } };
  const cleanup = () => {
    root.unmount();
    node.remove();
  };
  root.render(
    <StatusToastCard initial={initial} onDone={cleanup} controlsRef={controlsRef} />
  );
  return {
    update: (next) => controlsRef.current.update(next),
  };
}
